import { Component, inject } from '@angular/core';
import { ColDef, IServerSideGetRowsRequest } from 'ag-grid-community';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

import { ViewId } from 'src/app/api/services/view-preset-api.service';
import { AgDataObjectCellRendererComponent } from 'src/app/ag-grid/components/cell-renderers/ag-data-object-cell-renderer/ag-data-object-cell-renderer.component';
import {
  buildAuditTrailColumnGroup,
  buildOwnerColumnDef,
} from 'src/app/ag-grid/utils/audit-trail-columns.utils';
import { gridColumnHeader } from '@sensika/ag-grid-kit';
import { DataGridHostBase } from 'src/app/ag-grid/data-grid-host-base';
import { UserApiService } from 'src/app/api/services/user-api.service';
import { ObjectEditorService } from '../../services/object-editor.service';

@Component({
  selector: 'app-dimensions',
  templateUrl: './dimensions.component.html',
  standalone: false,
})
export class DimensionsComponent extends DataGridHostBase {
  /** i18n scanner K4: registers 'dimensions.grid.column.*' as used. */
  protected readonly i18nViewName = 'dimensions';

  private readonly userApi = inject(UserApiService);
  private readonly editService = inject(ObjectEditorService);

  readonly viewId = ViewId.Dimensions;
  readonly title = this.translateService.instant('grid.title.dimensions');
  readonly icon: IconProp = ['fas', 'layer-group'];

  readonly fetchRows = (request: IServerSideGetRowsRequest) =>
    this.userApi.getUserDimensionsAgGrid(request);

  readonly columnDefs: ColDef[] = this.buildColumnDefs();

  private columnHeader(key: string): string {
    return gridColumnHeader(this.translateService, this.i18nViewName, key);
  }

  private buildColumnDefs(): ColDef[] {
    return [
      {
        headerName: this.columnHeader('name'),
        field: 'title',
        filter: 'agTextColumnFilter',
        cellRenderer: AgDataObjectCellRendererComponent,
        cellRendererParams: {
          titleKey: 'title',
          objectId: 'object_id',
          objectClassId: 'object_class_id',
          objectTypeId: 'object_type_id',
          lifecycleStatus: 'lifecycle_status',
          onClick: (data) =>
            this.grid?.refreshWhenSaved(
              this.editService.loadFeedAndOpenPopup(data.id),
            ),
        },
        cellStyle: { overflow: 'visible' },
      },
      {
        headerName: this.columnHeader('description'),
        field: 'description',
        filter: 'agTextColumnFilter',
      },
      {
        headerName: this.translateService.instant('grid.column.count-used'),
        field: 'count_used_as_dimension',
        sortable: false,
        floatingFilter: false,
        suppressHeaderMenuButton: true,
        suppressHeaderFilterButton: true,
        suppressHeaderContextMenu: true,
        type: 'numericColumn',
        maxWidth: 160,
      },
      {
        headerName: this.translateService.instant('grid.column.workspace'),
        field: 'workspace.title',
        filter: 'agTextColumnFilter',
        cellRenderer: AgDataObjectCellRendererComponent,
        cellRendererParams: {
          object: 'workspace',
          titleKey: 'title',
          icon: ['fas', 'folder'],
          // Only refs the dispatcher can open are links.
          clickable: (ref) => this.editService.canOpenDataObjectRef(ref),
          onClick: (ref) => this.editService.openDataObjectRef(ref),
        },
        cellStyle: { overflow: 'visible' },
      },
      buildOwnerColumnDef(this.translateService, this.openUser),
      buildAuditTrailColumnGroup(this.translateService, {
        area: 'analytics',
        onUserClick: this.openUser,
      }),
    ];
  }
}
